import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useState } from 'react';

import { PatchMeasureInput, PostMeasureInput } from '@/api/creation';
import { Input } from '@/components/Elements/Input';
import { validationInputVoidCheck, validationMessageMinNumCheck } from '@/utils/inputMultiValidationCheck';

import { ProjectModalStatus } from './ProjectListView';

interface ModalProps {
  modalStatus: ProjectModalStatus;
  setModalStatus: React.Dispatch<React.SetStateAction<ProjectModalStatus>>;
  addMeasure: (input: PostMeasureInput) => void;
  editMeasure: (measureId: string, input: PatchMeasureInput) => void;
}

export default function ProjectModal(props: ModalProps) {
  const [inputText, setInputText] = useState(props.modalStatus.defaultValue);

  const [isErrorObject, setIsErrorObject] = useState({
    isError: false,
    errorMessage: '',
  });

  const setNgStatus = () => {
    props.setModalStatus((prev) => {
      return {
        ...prev,
        open: false,
      };
    });
    setIsErrorObject({ isError: false, errorMessage: '' });
  };

  const OkProcess = async () => {
    if (checkError(inputText)) return;

    if (props.modalStatus.status.type === 'create') {
      props.addMeasure({
        name: inputText,
      });
    } else {
      props.editMeasure(props.modalStatus.status.measureId, {
        name: inputText,
      });
    }
    props.setModalStatus((prev) => {
      return {
        ...prev,
        open: false,
      };
    });
  };

  /**
   * バリデーションチェック
   * 中身は inputMultiValidationCheck.tsx の関数を使用
   *
   * @param e イベント値
   */
  const checkError = (e_string: string) => {
    let errorObject = { isError: false, errorMessage: '' };

    if (!validationMessageMinNumCheck(e_string, 51)) {
      errorObject = { isError: true, errorMessage: '施策名は50文字以下で入力してください' };
    }

    if (!validationInputVoidCheck(e_string)) {
      errorObject = { isError: true, errorMessage: '施策名を入力してください' };
    }

    setIsErrorObject(errorObject);
    if (!errorObject.isError) {
      setInputText(e_string);
    }

    return errorObject.isError;
  };

  return (
    <Transition.Root show={props.modalStatus.open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {
          props.setModalStatus((prev) => {
            return {
              ...prev,
              open: false,
            };
          });
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 opacity-30 transition-opacity" style={{ background: 'rgba(107, 114, 128)' }} />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={() => {
                      setNgStatus();
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <div className=" sm:flex sm:items-start">
                    <div className="text-center sm:text-left">
                      <Dialog.Title as="h3" className="h-[24px] w-[408px] text-lg font-medium leading-6 text-gray-900">
                        施策を{props.modalStatus.status.type === 'create' ? '追加' : '編集'}
                      </Dialog.Title>
                    </div>
                  </div>
                </div>

                <div className="py-6">
                  <div className="mt-2">
                    <Input
                      id="text"
                      name="text"
                      type="text"
                      label="施策名"
                      className="border-0 text-black outline-none ring-0 focus:border-0 focus:outline-none"
                      defaultValue={props.modalStatus.defaultValue}
                      onChange={(e: React.SyntheticEvent) => {
                        checkError((e.target as HTMLInputElement).value);
                      }}
                      isError={isErrorObject.isError}
                    />
                    {isErrorObject.isError && (
                      <p className="mt-2 text-sm text-red-400" id="email-error">
                        {isErrorObject.errorMessage}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md px-[17px] py-[9px] text-sm font-medium leading-5 text-white shadow-sm sm:ml-3 sm:w-auto"
                    style={{ background: '#007CC2' }}
                    onClick={() => {
                      if (isErrorObject.isError) return;
                      OkProcess();
                    }}
                  >
                    {props.modalStatus.status.type === 'create' ? '登録' : '保存'}する
                  </button>

                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-[17px] py-[9px] text-sm font-medium leading-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => {
                      setNgStatus();
                    }}
                  >
                    キャンセル
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
